import React from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

// Honest, verifiable facts only — no fabricated efficacy/engagement metrics.
const STATS = [
  { value: '50', label: 'structured lessons across 6 project-based units' },
  { value: '34', label: 'CSTA K–12 computer-science skills tracked per student' },
  { value: '100%', label: 'browser-based — nothing to install' },
  { value: 'Free', label: 'for students to start building games' },
];

export const LandingPage: React.FC = () => {
  return (
    <div className="landing-container">
      {/* ── Nav ─────────────────────────────────────────────────── */}
      <header className="landing-header">
        <nav className="landing-nav">
          <div className="nav-brand">
            <span className="brand-emoji">🎮</span>
            <span className="brand-text">NS Gaming Studio</span>
          </div>
          <div className="nav-links">
            <a href="#for-districts" className="nav-link nav-link-ghost">For Districts</a>
            <Link to="/gallery" className="nav-link nav-link-ghost">Gallery</Link>
            <Link to="/login" className="nav-link">Sign In</Link>
            <Link to="/register" className="nav-link nav-link-primary">Get Started Free</Link>
          </div>
        </nav>
      </header>

      <main className="landing-main">

        {/* ── Hero ──────────────────────────────────────────────── */}
        <section className="hero-section">
          <div className="hero-content">
            <div className="hero-badge">✨ CSTA K–12 Standards Aligned</div>
            <h1 className="hero-title">
              Students Learn to Code
              <br />
              <span className="hero-highlight">by Building Real Games</span>
            </h1>
            <p className="hero-subtitle">
              NS Gaming Studio combines AI-assisted game development with a structured computer
              science curriculum — so students stay engaged while teachers stay in control.
            </p>

            <div className="hero-actions">
              <Link to="/register" className="btn btn-primary btn-large">
                🚀 Start Building Free
              </Link>
              <a href="#for-districts" className="btn btn-secondary btn-large">
                🏫 District Info →
              </a>
            </div>

            <div className="hero-social-proof">
              <span className="hsp-item">🔒 COPPA & FERPA Compliant</span>
              <span className="hsp-sep">·</span>
              <span className="hsp-item">📚 No prior coding needed</span>
              <span className="hsp-sep">·</span>
              <span className="hsp-item">🆓 Free for students</span>
            </div>
          </div>

          <div className="hero-visual">
            <div className="hero-game-cards">
              <div className="game-card-preview gc1">🚀</div>
              <div className="game-card-preview gc2">🧩</div>
              <div className="game-card-preview gc3">⚔️</div>
            </div>
          </div>
        </section>

        {/* ── Stats bar ─────────────────────────────────────────── */}
        <section className="stats-section">
          <div className="stats-inner">
            {STATS.map((stat, i) => (
              <div key={i} className="stat-item">
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── How It Works ──────────────────────────────────────── */}
        <section className="features-section">
          <h2 className="features-heading">How It Works</h2>
          <p className="features-subheading">Students go from zero to a working game in their first session — then keep leveling up.</p>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-step">1</div>
              <div className="feature-icon">💬</div>
              <h3>Chat with AI</h3>
              <p>Tell the AI what game you want to build. "Make me a space shooter with 3 levels" is all it takes to start.</p>
            </div>
            <div className="feature-card">
              <div className="feature-step">2</div>
              <div className="feature-icon">👀</div>
              <h3>Watch It Come Alive</h3>
              <p>See real HTML, CSS, and JavaScript appear in real-time. Students learn by reading and tweaking actual code.</p>
            </div>
            <div className="feature-card">
              <div className="feature-step">3</div>
              <div className="feature-icon">🎨</div>
              <h3>Make It Yours</h3>
              <p>Add features, change mechanics, remix classmates' games. Every iteration deepens their understanding.</p>
            </div>
          </div>
        </section>

        {/* ── Showcase ──────────────────────────────────────────── */}
        <section className="showcase-section">
          <h2>Games Students Have Built</h2>
          <p className="showcase-sub">Real projects from the student gallery — each one a portfolio piece.</p>
          <div className="showcase-grid">
            {[
              { emoji: '🚀', name: 'Galactic Hopper', detail: 'Platformer · 10 levels' },
              { emoji: '🧩', name: 'Color Cascade', detail: 'Puzzle · Chain combos' },
              { emoji: '⚔️', name: 'Shadow Knight', detail: 'Action RPG · Pixel art' },
              { emoji: '🏎️', name: 'Velocity GP', detail: 'Racing · AI opponent' },
              { emoji: '🌊', name: 'Ocean Explorer', detail: 'Adventure · Water FX' },
              { emoji: '🎵', name: 'Bounce Blitz', detail: 'Arcade · Web Audio API' },
            ].map((item, i) => (
              <div key={i} className="showcase-item">
                <div className="showcase-emoji">{item.emoji}</div>
                <div className="showcase-item-text">
                  <span className="showcase-name">{item.name}</span>
                  <span className="showcase-detail">{item.detail}</span>
                </div>
              </div>
            ))}
          </div>
          <Link to="/gallery" className="showcase-gallery-link">
            View the full student gallery →
          </Link>
        </section>

        {/* ── For Districts ─────────────────────────────────────── */}
        <section className="district-section" id="for-districts">
          <div className="district-inner">
            <div className="district-left">
              <div className="district-badge">🏫 FOR SCHOOL DISTRICTS</div>
              <h2 className="district-title">
                A Complete CS Curriculum,<br />
                <span className="district-highlight">Built for the Way Kids Learn Today</span>
              </h2>
              <p className="district-desc">
                NS Gaming Studio is more than a coding tool — it's a full K–8 computer science
                program with structured lessons, standards tracking, and real-time teacher insights.
                Deploy it district-wide in a single afternoon.
              </p>
              <div className="district-features">
                <div className="df-item">
                  <span className="df-icon">📋</span>
                  <div>
                    <strong>32 structured lessons</strong>
                    <span>6 units from HTML basics to portfolio capstone</span>
                  </div>
                </div>
                <div className="df-item">
                  <span className="df-icon">📊</span>
                  <div>
                    <strong>Teacher Dashboard</strong>
                    <span>Real-time progress, skill mastery, and assignment tracking per student</span>
                  </div>
                </div>
                <div className="df-item">
                  <span className="df-icon">🎯</span>
                  <div>
                    <strong>CSTA K–12 Aligned</strong>
                    <span>Every lesson maps to a specific CSTA standard — ready for your curriculum review</span>
                  </div>
                </div>
                <div className="df-item">
                  <span className="df-icon">🔒</span>
                  <div>
                    <strong>Student-Safe by Design</strong>
                    <span>COPPA & FERPA compliant, no ads, no data selling, SSO-ready</span>
                  </div>
                </div>
              </div>
              <div className="district-cta">
                <a href="mailto:hello@nsgamingstudio.com" className="btn btn-primary btn-large">
                  📩 Request a District Demo
                </a>
                <span className="district-cta-note">Free pilot for qualifying districts</span>
              </div>
            </div>

            <div className="district-right">
              <div className="teacher-dashboard-preview">
                <div className="tdp-header">
                  <span>👩‍🏫 Teacher Dashboard</span>
                  <span className="tdp-live">● LIVE</span>
                </div>
                <div className="tdp-stats-row">
                  <div className="tdp-stat">
                    <span className="tdp-stat-n">24</span>
                    <span className="tdp-stat-l">Students Active</span>
                  </div>
                  <div className="tdp-stat">
                    <span className="tdp-stat-n">87%</span>
                    <span className="tdp-stat-l">Lesson Completion</span>
                  </div>
                  <div className="tdp-stat">
                    <span className="tdp-stat-n">142</span>
                    <span className="tdp-stat-l">Games Published</span>
                  </div>
                </div>
                <div className="tdp-progress-list">
                  {[
                    { name: 'Priya N.', unit: 'Unit 3', pct: 78 },
                    { name: 'Marcus J.', unit: 'Unit 4', pct: 92 },
                    { name: 'Sofia E.', unit: 'Unit 2', pct: 55 },
                    { name: 'DeShawn W.', unit: 'Unit 5', pct: 100 },
                    { name: 'Lila C.', unit: 'Unit 3', pct: 67 },
                  ].map((s, i) => (
                    <div key={i} className="tdp-row">
                      <span className="tdp-name">{s.name}</span>
                      <span className="tdp-unit">{s.unit}</span>
                      <div className="tdp-bar-wrap">
                        <div className="tdp-bar" style={{ width: `${s.pct}%` }} />
                      </div>
                      <span className="tdp-pct">{s.pct}%</span>
                    </div>
                  ))}
                </div>
                <div className="tdp-footer">
                  🏆 Top skill this week: <strong>Canvas Animation</strong>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials removed: the product has no real customers/pilots yet,
            so fabricated quotes would be deceptive. Restore with REAL, attributed
            testimonials once a pilot provides them. */}

        {/* ── Final CTA ─────────────────────────────────────────── */}
        <section className="final-cta-section">
          <div className="final-cta-inner">
            <h2 className="final-cta-title">Ready to Transform CS Education at Your School?</h2>
            <p className="final-cta-sub">
              Join forward-thinking districts turning game creation into computer science mastery.
            </p>
            <div className="final-cta-actions">
              <Link to="/register" className="btn btn-primary btn-large">
                🎮 Start Free Today
              </Link>
              <a href="mailto:hello@nsgamingstudio.com" className="btn btn-outline btn-large">
                📩 Talk to Our Team
              </a>
            </div>
          </div>
        </section>

      </main>

      {/* ── Footer ────────────────────────────────────────────── */}
      <footer className="landing-footer">
        <div className="footer-content">
          <span>🎮 NS Gaming Studio</span>
          <span className="footer-sep">·</span>
          <span>Powered by NeuralSoft</span>
          <span className="footer-sep">·</span>
          <span>COPPA & FERPA Compliant</span>
          <span className="footer-sep">·</span>
          <Link to="/terms" className="footer-link">Terms</Link>
          <span className="footer-sep">·</span>
          <Link to="/privacy" className="footer-link">Privacy</Link>
        </div>
      </footer>
    </div>
  );
};
